import React, {useEffect, useRef, useState} from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  tramResendComparator,
  TramResendSortedTableFields,
} from '../../utils/tableUtils.ts';
import {StyledTableSortLabel} from './StyledTableSortLabel.ts';
import {visuallyHidden} from '@mui/utils';

interface DetectorCount {
  detector: string;
  count: number;
}

interface TrainData {
  train_number: string;
  model_number: string;
  avg_rssi: number;
  quota: number;
  trend: string;
  resend_rate: number;
  resend_spikes: number;
  worst_detector: string;
  resends_per_detector: DetectorCount[];
  trips_without_resend_flag: number;
  trips_with_resend_flag: number;
  problem_score: number;
}

interface Props {
  data: TrainData[];
  isLoading: boolean;
  error: Error;
}

const getScoreCategory = (
  score: number,
): {label: string; color: 'error' | 'warning' | 'success'} => {
  if (score >= 80) return {label: 'High Risk', color: 'error'};
  if (score >= 50) return {label: 'Moderate', color: 'warning'};
  return {label: 'Safe', color: 'success'};
};

const TrainResendTable: React.FC<Props> = ({data, isLoading, error}) => {
  // const { data = [], isLoading, error } = useFetchEventTrainResendStat();
  const [orderBy, setOrderBy] = useState<keyof TrainData>('trainNumber');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [search, setSearch] = useState('');
  const [dialogData, setDialogData] = useState<DetectorCount[] | null>(null);
  const [showAll, setShowAll] = useState(true);
  const [visibleRows, setVisibleRows] = useState(10);
  const [categoryFilter, setCategoryFilter] = useState('High Risk');

  const observer = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLTableRowElement | null>(null);

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof TrainData,
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  let _data = data ? data : [];
  _data = _data.map((row: never) => {
    return JSON.parse(row);
  });

  const filtered = _data.filter((item) => {
    const cat = getScoreCategory(item.problem_score).label;
    const matchesCategory = categoryFilter === 'All' || categoryFilter === cat;

    if(item.worst_detector){
     return  matchesCategory &&
      (item.train_number.toLowerCase().includes(search.toLowerCase()) ||
        item.model_number.toLowerCase().includes(search.toLowerCase()) ||
        item.worst_detector.toLowerCase().includes(search.toLowerCase()))
    }
    return (
      matchesCategory &&
      (item.train_number.toLowerCase().includes(search.toLowerCase()) ||
        item.model_number.toLowerCase().includes(search.toLowerCase()))
    );
  });

  const dataWithScore = filtered.map((row) => {
    const category = getScoreCategory(row.problem_score ?? 0);
    return {
      ...row,
      category: category,
    };
  });

  const sortedData = React.useMemo(
    () => [...dataWithScore].sort(tramResendComparator<TrainData>(order, orderBy)),
    [order, dataWithScore, orderBy],
  );

  const createSortHandler = (property: string) => (event: React.MouseEvent<unknown>) => {
    handleRequestSort(event, property);
  };

  // const topWorstFiltered = sorted.sort((a, b) => b.problem_score - a.problem_score);

  const _visibleData = showAll ? sortedData : sortedData.slice(0, 10);
  const visibleData = _visibleData.filter((item) => item.train_number.length <= 5);

  useEffect(() => {
    if (!showAll || !loadMoreRef.current) return;
    if (observer.current) observer.current.disconnect();

    observer.current = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setVisibleRows((prev) => Math.min(prev + 10, sortedData.length));
      }
    });
    observer.current.observe(loadMoreRef.current);
  }, [showAll, visibleRows, sortedData.length]);

  const formatPreview = (detectors: DetectorCount[]) => {
    const preview = detectors
      .slice(0, 3)
      .map((d) => `${d.detector} (${d.count})`)
      .join(', ');
    const more = detectors.length > 3 ? ` +${detectors.length - 3} more` : '';
    return preview + more;
  };

  return (
    <Paper style={{width: '100%', overflowY: 'auto'}}>
      <Box display="flex" gap={1} p={2} alignItems="center">
        <Box flex={5}>
          <TextField
            label="Search"
            variant="outlined"
            size="small"
            fullWidth
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Box>
        <Box flex={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Category</InputLabel>
            <Select
              label="Category"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <MenuItem value="All">All</MenuItem>
              <MenuItem value="High Risk">High Risk</MenuItem>
              <MenuItem value="Moderate">Moderate</MenuItem>
              <MenuItem value="Safe">Safe</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Box flex={2}>
          <Button fullWidth variant="outlined" onClick={() => setShowAll(!showAll)}>
            {showAll ? 'Show Top 10' : 'Show All'}
          </Button>
        </Box>
      </Box>

      {isLoading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Box color="error.main" p={4}>
          Error: {error.message}
        </Box>
      ) : (
        <TableContainer sx={{height: '100vh'}}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {[
                  'train',
                  'model',
                  'avgRssi',
                  'trend',
                  'resendRate',
                  'detWithFlag',
                  'detWithoutFlag',
                  'worstDetector',
                  'resendsPerDetector',
                  'score',
                  'category',
                ].map((key) =>
                  key === 'worstDetector' || key === 'resendsPerDetector' ? (
                    <TableCell key={key}>{key}</TableCell>
                  ) : (
                    <TableCell
                      sx={{position: 'sticky', left: 0, zIndex: 3}}
                      key={key}
                      sortDirection={orderBy === key ? order : false}
                    >
                      <StyledTableSortLabel
                        active={orderBy === TramResendSortedTableFields[key]['key']}
                        direction={
                          orderBy === TramResendSortedTableFields[key]['key']
                            ? order
                            : 'asc'
                        }
                        onClick={createSortHandler(
                          TramResendSortedTableFields[key]['key'],
                        )}
                      >
                        {TramResendSortedTableFields[key]['text']}
                        {orderBy === TramResendSortedTableFields[key]['key'] ? (
                          <Box component="span" sx={visuallyHidden}>
                            {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                          </Box>
                        ) : null}
                      </StyledTableSortLabel>
                    </TableCell>
                  ),
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleData.map((row, index) => {
                const category = getScoreCategory(row.problem_score);
                return (
                  <TableRow key={index}>
                    <TableCell>{row.train_number}</TableCell>
                    <TableCell>{row.model_number}</TableCell>
                    <TableCell>{row.avg_rssi}</TableCell>
                    <TableCell>{row.trend}</TableCell>
                    <TableCell>{row.resend_rate}%</TableCell>
                    <TableCell>{row.trips_with_resend_flag}</TableCell>
                    <TableCell>{row.trips_without_resend_flag}</TableCell>
                    <TableCell>{row.worst_detector}</TableCell>
                    <TableCell>
                      <Tooltip
                        title={row.resends_per_detector
                          .map((d) => `${d.detector} (${d.count})`)
                          .join(', ')}
                        placement="top"
                      >
                        <span>{formatPreview(row.resends_per_detector)}</span>
                      </Tooltip>
                      <Button
                        size="small"
                        onClick={() => setDialogData(row.resends_per_detector)}
                      >
                        View
                      </Button>
                    </TableCell>
                    <TableCell>
                      <Tooltip
                        title={`Score = -0.6*TripsWithFlag, -0.5*Quota, -0.4*Spikes, -0.3*Gap, -0.2*Rate, +0.1*RSSI, +0.2*TripsWithoutFlag`}
                      >
                        <span>{row.problem_score?.toFixed(2)}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Chip label={category.label} color={category.color} size="small" />
                    </TableCell>
                  </TableRow>
                );
              })}
              {showAll && visibleRows < sortedData.length && (
                <TableRow ref={loadMoreRef}>
                  <TableCell colSpan={11} align="center">
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={!!dialogData} onClose={() => setDialogData(null)}>
        <DialogTitle>Resends Per Detector</DialogTitle>
        <DialogContent>
          <List>
            {dialogData?.map((d, idx) => (
              <ListItem
                key={idx}
                sx={{bgcolor: '#f5f5f5', mb: 1, borderRadius: 2, px: 2, py: 1}}
              >
                <ListItemText
                  primary={
                    <Typography variant="body1" fontWeight="bold" color="primary">
                      {d.detector}
                    </Typography>
                  }
                  secondary={
                    <Typography variant="body2" color="text.secondary">
                      Resend Count: {d.count}
                    </Typography>
                  }
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
      </Dialog>
    </Paper>
  );
};

export default TrainResendTable;
